"use client";

import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SystemRoleOption {
  value: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

export interface SystemRoleSelectorV2Props {
  value: string;
  onChange: (value: string) => void;
  options: SystemRoleOption[];
  className?: string;
}

export function SystemRoleSelectorV2({
  value,
  onChange,
  options,
  className,
}: SystemRoleSelectorV2Props) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-stretch",
        className
      )}
      role="radiogroup"
    >
      {options.map((option) => {
        const isSelected = value === option.value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onChange(option.value)}
            className={cn(
              "group relative flex h-full min-h-[9.5rem] w-full flex-col gap-3 rounded-2xl border p-4 text-left transition-all duration-200",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              isSelected
                ? "border-indigo-500 bg-gradient-to-br from-indigo-50/90 via-white to-violet-50/70 shadow-[0_10px_28px_-14px_rgba(79,70,229,0.55)] dark:border-indigo-400/80 dark:from-indigo-950/50 dark:via-zinc-900 dark:to-violet-950/30"
                : "border-slate-200/90 bg-white hover:border-indigo-200 hover:bg-slate-50/90 dark:border-zinc-800 dark:bg-zinc-900/70 dark:hover:border-indigo-500/30 dark:hover:bg-zinc-900"
            )}
          >
            {isSelected ? (
              <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500 text-white shadow-sm">
                <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
              </span>
            ) : null}

            <span
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors",
                isSelected
                  ? "bg-indigo-500 text-white shadow-sm"
                  : "bg-slate-100 text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-600 dark:bg-zinc-800 dark:text-zinc-400 dark:group-hover:bg-indigo-950/60"
              )}
            >
              {option.icon}
            </span>

            <div className="flex min-h-0 flex-1 flex-col gap-1.5 pr-6">
              <span
                className={cn(
                  "text-sm font-semibold leading-tight",
                  isSelected
                    ? "text-indigo-700 dark:text-indigo-200"
                    : "text-slate-900 dark:text-zinc-100"
                )}
              >
                {option.title}
              </span>
              <span className="text-xs leading-relaxed text-slate-500 dark:text-zinc-400">
                {option.description}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
